import { useMemo, useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const fmt = (v) => `£${Number(v||0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Pure simulator: compares cost of paying the SAME monthly payment toward
// (A) the existing debt at its current APR vs
// (B) a new 0% balance-transfer card (with one-off transfer fee + post-promo APR).
// Cap simulation horizon at 60 months for safety.
function simulate({ balance, apr, monthly, months, postPromoApr = 0, promoMonths = 0, feePct = 0 }) {
  const HORIZON = 60;
  let bal = balance;
  let interestPaid = 0;
  let monthsTaken = HORIZON;
  for (let m = 1; m <= HORIZON; m++) {
    const rate = (m <= promoMonths ? 0 : apr) / 100 / 12;
    const interest = bal * rate;
    interestPaid += interest;
    const principal = Math.min(bal + interest, monthly) - interest;
    bal = Math.max(0, bal - principal);
    if (bal <= 0.01) { monthsTaken = m; break; }
  }
  return {
    interestPaid: Math.round(interestPaid * 100) / 100,
    monthsTaken,
    fee: Math.round(balance * (feePct/100) * 100) / 100,
  };
}

function parseApr(str) {
  if (typeof str === "number") return str;
  if (!str) return 0;
  const m = String(str).match(/[\d.]+/);
  return m ? parseFloat(m[0]) : 0;
}

export default function BalanceTransferDialog({ open, onClose, debts }) {
  const eligible = (debts || []).filter(d => d.total_balance > 0);
  const [debtId, setDebtId] = useState("");
  const [promoMonths, setPromoMonths] = useState(18);
  const [feePct, setFeePct] = useState(2.9);
  const [postPromoApr, setPostPromoApr] = useState(24.9);
  const [monthly, setMonthly] = useState(150);

  // Auto-pick highest APR debt when opened or list changes
  useEffect(() => {
    if (!open) return;
    if (debtId && eligible.find(d => d.id === debtId)) return;
    const top = [...eligible].sort((a, b) => parseApr(b.apr_now) - parseApr(a.apr_now))[0];
    if (top) {
      setDebtId(top.id);
      setMonthly(Math.max(top.statement_min || 0, 50));
    }
  }, [open, eligible, debtId]);

  const debt = eligible.find(d => d.id === debtId);

  const result = useMemo(() => {
    if (!debt) return null;
    const balance = Number(debt.total_balance || 0);
    const apr = parseApr(debt.apr_now);
    const m = Math.max(1, Number(monthly) || 0);
    const current = simulate({ balance, apr, monthly: m, months: 60 });
    const transferred = simulate({
      balance, apr: postPromoApr, monthly: m, months: 60,
      postPromoApr, promoMonths: Number(promoMonths) || 0, feePct: Number(feePct) || 0,
    });
    const totalCurrent = current.interestPaid;
    const totalTransfer = transferred.interestPaid + transferred.fee;
    const savings = Math.round((totalCurrent - totalTransfer) * 100) / 100;
    return { current, transferred, totalCurrent, totalTransfer, savings, apr };
  }, [debt, monthly, promoMonths, feePct, postPromoApr]);

  const verdict = result
    ? (result.savings > 100 ? { label:`SAVE ${fmt(result.savings)}`, cls:"text-green-700 bg-green-50 border-green-300" }
      : result.savings > 0  ? { label:`Marginal — saves ${fmt(result.savings)}`, cls:"text-amber-700 bg-amber-50 border-amber-300" }
      : { label:`Not worth it — costs ${fmt(-result.savings)} more`, cls:"text-red-700 bg-red-50 border-red-300" })
    : null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl rounded-sm border border-gray-200 max-h-[90vh] overflow-y-auto" data-testid="balance-transfer-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg font-semibold tracking-tight">
            Balance Transfer Simulator
          </DialogTitle>
          <p className="text-xs text-gray-500 mt-1">See if moving a balance to a new 0% card actually saves money after fees.</p>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Debt to transfer</Label>
            <select
              value={debtId}
              onChange={(e) => setDebtId(e.target.value)}
              data-testid="bt-debt-select"
              className="mt-1 w-full border border-gray-300 rounded-sm px-3 py-2 text-sm font-mono-data bg-white"
            >
              <option value="">Select a debt…</option>
              {eligible.map(d => (
                <option key={d.id} value={d.id}>
                  {d.name} — {fmt(d.total_balance)} @ {d.apr_now}
                </option>
              ))}
            </select>
          </div>

          <div className="border border-gray-200 rounded-sm p-3 bg-gray-50 space-y-3">
            <h4 className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">New balance-transfer offer</h4>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-gray-500">0% Promo (months)</Label>
                <Input data-testid="bt-promo-months" type="number" min="1" max="36" value={promoMonths} onChange={e => setPromoMonths(e.target.value)} className="rounded-sm mt-1 font-mono-data" />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Transfer fee (%)</Label>
                <Input data-testid="bt-fee-pct" type="number" step="0.1" min="0" max="10" value={feePct} onChange={e => setFeePct(e.target.value)} className="rounded-sm mt-1 font-mono-data" />
              </div>
              <div>
                <Label className="text-xs text-gray-500">APR after promo (%)</Label>
                <Input data-testid="bt-post-apr" type="number" step="0.1" min="0" value={postPromoApr} onChange={e => setPostPromoApr(e.target.value)} className="rounded-sm mt-1 font-mono-data" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Monthly payment (£) — same for both scenarios</Label>
              <Input data-testid="bt-monthly" type="number" step="1" min="1" value={monthly} onChange={e => setMonthly(e.target.value)} className="rounded-sm mt-1 font-mono-data w-32" />
            </div>
          </div>

          {result && (
            <div className="grid grid-cols-2 gap-3">
              <div className="border border-gray-200 rounded-sm p-3" data-testid="bt-current-card">
                <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-2">Stay on current card</div>
                <div className="text-xs text-gray-500">APR: <span className="font-mono-data text-gray-800">{result.apr.toFixed(2)}%</span></div>
                <div className="text-xs text-gray-500 mt-1">Interest paid: <span className="font-mono-data text-red-700 font-semibold">{fmt(result.current.interestPaid)}</span></div>
                <div className="text-xs text-gray-500 mt-1">Months to clear: <span className="font-mono-data text-gray-800">{result.current.monthsTaken >= 60 ? "60+" : result.current.monthsTaken}</span></div>
                <div className="text-xs text-gray-500 mt-1">Total cost: <span className="font-mono-data text-red-700 font-semibold">{fmt(result.totalCurrent)}</span></div>
              </div>
              <div className="border border-blue-300 bg-blue-50/30 rounded-sm p-3" data-testid="bt-transfer-card">
                <div className="text-[10px] uppercase tracking-widest text-blue-700 font-semibold mb-2">Transfer to 0% card</div>
                <div className="text-xs text-gray-500">Promo: <span className="font-mono-data text-gray-800">{promoMonths} months @ 0%</span></div>
                <div className="text-xs text-gray-500 mt-1">Transfer fee: <span className="font-mono-data text-amber-700 font-semibold">{fmt(result.transferred.fee)}</span></div>
                <div className="text-xs text-gray-500 mt-1">Interest paid: <span className="font-mono-data text-amber-700 font-semibold">{fmt(result.transferred.interestPaid)}</span></div>
                <div className="text-xs text-gray-500 mt-1">Months to clear: <span className="font-mono-data text-gray-800">{result.transferred.monthsTaken >= 60 ? "60+" : result.transferred.monthsTaken}</span></div>
                <div className="text-xs text-gray-500 mt-1">Total cost: <span className="font-mono-data text-blue-700 font-semibold">{fmt(result.totalTransfer)}</span></div>
              </div>
            </div>
          )}

          {verdict && (
            <div className={`border rounded-sm px-4 py-3 font-semibold text-sm ${verdict.cls}`} data-testid="bt-verdict">
              {verdict.label}
            </div>
          )}

          {!debt && (
            <div className="text-xs text-gray-400 text-center py-3">Pick a debt above to run the simulation.</div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" onClick={onClose} className="rounded-sm bg-black text-white hover:bg-gray-800" data-testid="bt-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
